<template>
  <section
    id="hero"
    class="padding-bottom-xl"
  >
    <div
      style="background-color: var(--white)"
      class="row align-middle position-relative"
    >
      <!-- Left Icon Grid -->
      <icon-grid
        :icon-class-matrix="iconClassMatrix"
        :primary-color="primaryColor"
        :color-transition="colorTransition"
        :grid-config="leftGridConfig"
        :current-family="currentFamilyStyle.family"
        :current-style="currentFamilyStyle.style"
        :current-pack-name="currentPackName"
        position="left"
      />

      <!-- Center Content -->
      <div class="text-center padding-xl column-8 laptop:column-4 desktop:column-3">
        <span
          role="button"
          tabindex="0"
          class="display-block"
          :aria-label="`${currentFamilyStyleLabel}!`"
          aria-describedby="hero-randomize-desc"
          data-balloon-pos="up"
          :data-balloon-visible="isAutoRandomizing"
          :style="logoStyle"
          @click="randomize"
          @keydown.enter="randomize"
          @keydown.space.prevent="randomize"
          @mousedown.prevent
        >
          <i :class="logoIconClass" aria-hidden="true" />
          <span id="hero-randomize-desc" class="sr-only">Click to randomize icon style</span>
        </span>

        <h1 class="margin-top-xs margin-bottom-0">
          <!-- prettier-ignore -->
          <span>
            <span :style="{ transition: colorTransition, color: taglineColors.logo.text }">Font Awesome</span><span :style="{ transition: colorTransition, color: taglineColors.logo.period }">.</span><br />
            <span :style="{ transition: colorTransition, color: taglineColors.first.text }">Icons</span><span :style="{ transition: colorTransition, color: taglineColors.first.period }">.</span>
            <span :style="{ transition: colorTransition, color: taglineColors.second.text }">Easy</span><span :style="{ transition: colorTransition, color: taglineColors.second.period }">.</span>
            <span :style="{ transition: colorTransition, color: taglineColors.third.text }">Done</span><span :style="{ transition: colorTransition, color: taglineColors.third.period }">.</span>
          </span>
        </h1>
        <p class="size-lg margin-y-2xl text-wrap-balance">The internet's favorite icon library &amp; toolkit.</p>
        <form
          class="icons-search form line-length-sm padding-bottom-xl margin-auto"
          style="--form-button-margin: 0"
          @submit.prevent="search"
        >
          <div
            class="with-icon-before"
            style="--input-with-icon-color: var(--fa-navy); --input-border-color: var(--fa-navy); --input-placeholder-color: var(--fa-md-gravy)"
          >
            <font-awesome-icon
              :icon="$v7.solid.faMagnifyingGlass"
              style="line-height: 1"
            />
            <label
              for="icons-search"
              class="sr-only"
            >
              Search for Icons
            </label>
            <input
              id="icons-search"
              v-model="query"
              type="text"
              name="icons-search"
              class="rounded"
              :placeholder="`Find the perfect icon...`"
            />
            <button
              type="submit"
              class="sr-only"
            >
              Search for Icons
            </button>
          </div>
        </form>
        <dl
          class="hero-stats margin-0 padding-0 display-flex flex-wrap flex-items-center flex-content-center"
          :style="{
            'gap': 'var(--spacing-md)',
            '--link-color': computedLinkColor,
            'transition': colorTransition
          }"
        >
          <dt class="sr-only">About Font Awesome Icons</dt>
          <dd class="size-sm margin-bottom-0">
            <font-awesome-icon
              :icon="$v7.solid.faHashtag"
              size="lg"
              class="margin-right-3xs"
            />
            <site-link :to="{ name: 'changelog' }">v{{ $releases.active.version }}</site-link>
          </dd>
          <dd class="size-sm margin-bottom-0">
            <font-awesome-icon
              :icon="$v7.solid.faShapes"
              size="lg"
              class="margin-right-3xs"
            />
            <site-link :to="$search()"> {{ $counts.icons.all.formatted }} Icons </site-link>
          </dd>
          <dd class="size-sm margin-bottom-0">
            <font-awesome-icon
              :icon="$v7.solid.faPalette"
              size="lg"
              class="margin-right-3xs"
            />
            <site-link :to="$icons()"> {{ $counts.totalLatestStylesWithoutBrands }} Icon Styles </site-link>
          </dd>
        </dl>
      </div>

      <!-- Right Icon Grid -->
      <icon-grid
        :icon-class-matrix="iconClassMatrix"
        :primary-color="primaryColor"
        :color-transition="colorTransition"
        :grid-config="rightGridConfig"
        :current-family="currentFamilyStyle.family"
        :current-style="currentFamilyStyle.style"
        :current-pack-name="currentPackName"
        position="right"
      />
    </div>

    <!-- Video Section -->
    <div
      id="fa7-video"
      class="container padding-top-4xl"
    >
      <div class="row align-bottom laptop:align-top padding-bottom-lg roomy position-relative">
        <div class="column-12 laptop:column-8 padding-bottom-lg laptop:padding-bottom-0">
          <div :style="videoContainerStyle">
            <iframe
              v-if="isVimeoAllowed"
              :src="videoUrl"
              title="Font Awesome product overview video"
              frameborder="0"
              webkitAllowFullScreen
              mozallowfullscreen
              allowFullScreen
              style="position: absolute; top: 0; left: 0; width: 100%; height: 100%"
            />

            <a
              v-else
              :href="videoUrl"
              target="_blank"
            >
              <img
                :src="placeholderImage"
                frameborder="0"
                style="position: absolute; top: 0; left: 0; width: 100%; height: 100%"
              />
            </a>
          </div>
        </div>
        <div class="column-8 offset-2 tablet:column-8 tablet:offset-2 laptop:column-4 laptop:offset-0 padding-top-4xl laptop:padding-top-0">
          <sale-cta />
        </div>
      </div>
    </div>
  </section>
</template>

<script>
  // Constants
  const GRID_CONFIG = {
    numRows: 8,
    numCols: 12,
    gap: '1.1rem',
    fontSize: '1.9rem',
    iconGap: '1.6rem',
    marginOffset: '1.5rem',
    rotationAngle: 15
  }

  const ICON_PATTERN_MULTIPLIER = 5

  const ICONS = [
    'user',
    'comment',
    'plane',
    'lightbulb',
    'music',
    'file',
    'camera',
    'volume',
    'film',
    'truck',
    'heart',
    'shirt',
    'globe',
    'suitcase',
    'house',
    'face-smile',
    'thumbs-up',
    'book',
    'headphones',
    'alarm-clock',
    'gear',
    'gift'
  ]

  const VIDEO_CONFIG = {
    width: 638,
    height: 360,
    url: 'https://player.vimeo.com/video/1065600738',
    placeholderImage: 'https://img.fortawesome.com/1ce05b4b/vimeoplaceholder-v7.png'
  }

  const AUTO_RANDOMIZE_CONFIG = {
    initialDelay: 5000,
    interval: 5000
  }

  const LOGO_CONFIG = {
    animationDuration: 500,
    animationClass: 'animate__animated animate__faster animate__bounceIn'
  }

  import { EventTrackingMixin } from 'app/mixins'
  import { Marketing } from '../../mixins'
  import { getFunctionalityPreferences } from 'app/store/modules/cookiePreferences'
  import { getIconPacks, getIconPacksByFamily } from 'app/views/search/collections.js'
  import startCase from 'lodash/startCase'
  import { sample, without } from 'lodash'
  import IconGrid from './IconGrid'
  import SaleCta from '../../components/SaleCta'
  import { THEME_COLORS } from './theme'

  export default {
    name: 'Hero',

    components: {
      IconGrid,
      SaleCta
    },

    mixins: [EventTrackingMixin, Marketing],

    data() {
      return {
        colorTransition: 'color var(--timing-xfast) ease-in-out',
        currentFamilyStyle: { family: 'classic', style: 'light' },
        currentTheme: THEME_COLORS.default,
        hasInitialAutoRandomized: false,
        isAutoRandomizing: false,
        isAutoRandomizingPaused: false,
        logoAnimationClass: '',
        logoAnimationTimeout: null,
        query: ''
      }
    },

    computed: {
      activeMajorVersion() {
        return this.$releases.active.versionParts.major
      },

      computedLinkColor() {
        return this.isDefaultTheme ? THEME_COLORS.blue.primary : this.primaryColor
      },

      currentFamilyStyleLabel() {
        return startCase(`${this.currentFamilyStyle.family} ${this.currentFamilyStyle.style}`)
      },

      currentPackName() {
        const iconPacks = getIconPacksByFamily(this.activeMajorVersion, this.currentFamilyStyle.family)
        const currentPack = iconPacks.find((pack) => pack.familyStyles.some((fs) => fs.style === this.currentFamilyStyle.style))
        return currentPack?.name || 'classic'
      },

      familyStyles() {
        const iconPacks = getIconPacks(this.activeMajorVersion, true) // exclude brands
        const familyStyles = []

        iconPacks.forEach((pack) => {
          pack.familyStyles.forEach((fs) => {
            familyStyles.push({
              family: fs.family,
              style: fs.style
            })
          })
        })

        return familyStyles
      },

      iconClassMatrix() {
        return this.generateIconMatrix()
      },

      isDefaultTheme() {
        return this.currentTheme === THEME_COLORS.default
      },

      isVimeoAllowed() {
        return getFunctionalityPreferences()
      },

      leftGridConfig() {
        return {
          ...GRID_CONFIG,
          alignment: 'flex-end',
          marginDirection: 'right'
        }
      },

      logoClass() {
        return `fa-${this.currentFamilyStyle.family} fa-${this.currentFamilyStyle.style}`
      },

      logoIconClass() {
        return [...this.logoClass.split(' '), 'fa-font-awesome', 'fa-4x', this.logoAnimationClass]
      },

      logoStyle() {
        return {
          '--balloon-color': this.secondaryColor,
          '--balloon-text-color': 'var(--text-color)',
          'cursor': 'pointer'
        }
      },

      placeholderImage() {
        return VIDEO_CONFIG.placeholderImage
      },

      primaryColor() {
        return this.currentTheme.primary
      },

      rightGridConfig() {
        return {
          ...GRID_CONFIG,
          alignment: 'flex-start',
          marginDirection: 'left'
        }
      },

      secondaryColor() {
        return this.currentTheme.secondary
      },

      taglineColors() {
        if (this.isDefaultTheme) {
          return {
            logo: { text: 'var(--text-color)', period: 'var(--text-color)' },
            first: { text: 'var(--fa-dk-purple)', period: 'var(--fa-dk-purple)' },
            second: { text: 'var(--fa-dk-teal)', period: 'var(--fa-dk-teal)' },
            third: { text: 'var(--fa-dk-blue)', period: 'var(--fa-dk-blue)' }
          }
        } else {
          return {
            logo: { text: this.primaryColor, period: 'var(--text-color)' },
            first: { text: 'var(--text-color)', period: this.primaryColor },
            second: { text: 'var(--text-color)', period: this.primaryColor },
            third: { text: 'var(--text-color)', period: this.primaryColor }
          }
        }
      },

      videoContainerStyle() {
        return {
          position: 'relative',
          paddingBottom: `calc(${VIDEO_CONFIG.height} / ${VIDEO_CONFIG.width} * 100%)`,
          overflow: 'hidden',
          borderRadius: 'var(--border-radius-lg)',
          maxWidth: '100%'
        }
      },

      videoUrl() {
        return VIDEO_CONFIG.url
      }
    },

    mounted() {
      this.startAutoRandomize()
    },

    beforeDestroy() {
      this.stopAutoRandomize()
      if (this.logoAnimationTimeout) {
        clearTimeout(this.logoAnimationTimeout)
        this.logoAnimationTimeout = null
      }
    },

    methods: {
      autoRandomize() {
        this.isAutoRandomizing = true
        this.performRandomization(false)

        // Mark that initial auto-randomization has occurred
        this.hasInitialAutoRandomized = true

        // Restart the timer for next auto-randomization
        this._autoRandomizeTimer = setTimeout(() => {
          this.autoRandomize()
        }, AUTO_RANDOMIZE_CONFIG.interval)

        // Clear the auto-randomizing indicator halfway through the interval
        setTimeout(() => {
          this.isAutoRandomizing = false
        }, AUTO_RANDOMIZE_CONFIG.interval / 2)
      },

      generateIconMatrix() {
        const { numRows, numCols } = GRID_CONFIG
        const familyClass = `fa-${this.currentFamilyStyle.family}`
        const styleClass = `fa-${this.currentFamilyStyle.style}`

        return Array.from({ length: numRows }, (_, rowIndex) =>
          Array.from({ length: numCols }, (_, colIndex) => {
            const icon = ICONS[(colIndex + rowIndex * ICON_PATTERN_MULTIPLIER) % ICONS.length]
            return [familyClass, styleClass, `fa-${icon}`]
          })
        )
      },

      getRandomFamilyStyle() {
        const availableStyles = this.familyStyles.filter((fs) => fs.family !== this.currentFamilyStyle.family || fs.style !== this.currentFamilyStyle.style)

        // If no different styles available, return current style
        if (availableStyles.length === 0) {
          return this.currentFamilyStyle
        }

        return availableStyles[Math.floor(Math.random() * availableStyles.length)]
      },

      getRandomTheme() {
        const nonDefaultThemes = without(Object.values(THEME_COLORS), THEME_COLORS.default)
        return sample(nonDefaultThemes)
      },

      performRandomization(trackEvent = false) {
        try {
          const newFamilyStyle = this.getRandomFamilyStyle()
          this.currentFamilyStyle = newFamilyStyle
          this.currentTheme = this.getRandomTheme()
          this.logoAnimationClass = LOGO_CONFIG.animationClass

          if (trackEvent) {
            this.$posthog.capture('home_hero:logo_randomize_click', {
              newFamilyStyle,
              newTheme: this.currentTheme
            })
          }

          // Clear any existing animation timeout
          if (this.logoAnimationTimeout) {
            clearTimeout(this.logoAnimationTimeout)
          }

          this.logoAnimationTimeout = setTimeout(() => {
            this.logoAnimationClass = ''
            this.logoAnimationTimeout = null
          }, LOGO_CONFIG.animationDuration)

          return true
        } catch (error) {
          // Fallback to default theme if randomization fails
          this.currentTheme = THEME_COLORS.default
          return false
        }
      },

      randomize() {
        if (!this.isAutoRandomizingPaused) {
          this.stopAutoRandomize()
          this.isAutoRandomizingPaused = true
        } else {
          this.performRandomization(true)
          this.startAutoRandomize()
          this.isAutoRandomizingPaused = false
        }
      },

      startAutoRandomize() {
        // Start auto-randomization after AUTO_RANDOMIZE_CONFIG's initial delay
        this._autoRandomizeTimer = setTimeout(() => {
          this.autoRandomize()
        }, AUTO_RANDOMIZE_CONFIG.initialDelay)
      },

      stopAutoRandomize() {
        if (this._autoRandomizeTimer) {
          clearTimeout(this._autoRandomizeTimer)
          this._autoRandomizeTimer = null
        }
      },

      search() {
        const trimmedQuery = (this.query || '').trim()
        if (!trimmedQuery) {
          return
        }

        this.$router.push({
          name: 'latest.search',
          query: { q: trimmedQuery }
        })
      }
    }
  }
</script>
